import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  Box,
  Tabs,
  Tab,
  Container,
  CircularProgress,
  Backdrop,
  Button
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import OfficeConfigManagement from '../components/admin/OfficeConfigManagement'
import StatisticsDashboard from '../components/admin/StatisticsDashboard'
import UserManagement from '../components/admin/UserManagement'
import { useAnalytics } from '../contexts/AnalyticsContext'
import { sessionState } from '../state'
import { AnalyticsEvents } from '../types/AnalyticsEvents'

import routes from './routes'

/**
 * AdminPage Component
 *
 * Main administrative interface with tab navigation for:
 * - Statistics Dashboard
 * - User Management
 * - Office Configuration
 *
 * Includes route guard to ensure only admins can access this page.
 *
 * Requirements: 2.3, 2.4, 5.1, 5.2, 5.3, 5.4, 5.5, 9.1
 */

type TabValue = 'statistics' | 'users' | 'offices'

const AdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const session = useRecoilValue(sessionState)
  const user = session.user
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const { trackEvent } = useAnalytics()

  // Get initial tab from URL or default to statistics
  const getInitialTab = (): TabValue => {
    const tabParam = searchParams.get('tab')
    if (
      tabParam === 'users' ||
      tabParam === 'offices' ||
      tabParam === 'statistics'
    ) {
      return tabParam
    }
    return 'statistics'
  }

  const [selectedTab, setSelectedTab] = useState<TabValue>(getInitialTab())
  const [hasRedirected, setHasRedirected] = useState(false)

  // Route guard: Check if user is admin
  useEffect(() => {
    // Only check once session is loaded
    if (session.isAuthenticating || session.isLoadingData) {
      return
    }

    // Check admin status
    if (!user || !user.isAdmin) {
      // User is not admin, redirect to home (only once)
      if (!hasRedirected) {
        setHasRedirected(true)
        // Use window.location for hard redirect to ensure it works
        window.location.href = routes.home
      }
    } else {
      // User is admin, allow access
      setIsCheckingAuth(false)
    }
  }, [user, session.isAuthenticating, session.isLoadingData, hasRedirected])

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: selectedTab }, { replace: true })
  }, [selectedTab, setSearchParams])

  // Restore tab from URL on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (
      tabParam === 'users' ||
      tabParam === 'offices' ||
      tabParam === 'statistics'
    ) {
      setSelectedTab(tabParam)
    }
  }, [searchParams])

  // Show loading state while checking admin status
  if (isCheckingAuth || session.isAuthenticating || session.isLoadingData) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    )
  }

  // If user is not admin and we haven't redirected yet, show loading
  // This prevents flash of admin content before redirect
  if (!user || !user.isAdmin) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    )
  }

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: TabValue
  ) => {
    setSelectedTab(newValue)

    // Track admin tab views
    try {
      if (newValue === 'statistics') {
        trackEvent(AnalyticsEvents.ADMIN_STATISTICS_VIEWED, {
          action_type: 'view_statistics_dashboard'
        })
      }
    } catch (error) {
      // Analytics errors should not break functionality
      console.error('Failed to track admin tab change:', error)
    }
  }

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(routes.home)}
        >
          Back to Portal
        </Button>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label='admin section tabs'
          variant='scrollable'
          scrollButtons='auto'
        >
          <Tab label='Statistics' value='statistics' />
          <Tab label='User Management' value='users' />
          <Tab label='Office Configuration' value='offices' />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box role='tabpanel' hidden={selectedTab !== 'statistics'}>
        {selectedTab === 'statistics' && <StatisticsDashboard />}
      </Box>

      <Box role='tabpanel' hidden={selectedTab !== 'users'}>
        {selectedTab === 'users' && (
          <UserManagement
            currentUserId={user?.uid || ''}
            currentUserEmail={user?.email || ''}
          />
        )}
      </Box>

      <Box role='tabpanel' hidden={selectedTab !== 'offices'}>
        {selectedTab === 'offices' && (
          <OfficeConfigManagement
            currentUserId={user?.uid || ''}
            currentUserEmail={user?.email || ''}
          />
        )}
      </Box>
    </Container>
  )
}

export default AdminPage
